// Scenario 3: past_due_us (default orange severity)
// US tenant, FOUNDATION x 5 seats, USD monthly. Last charge failed 3 days ago.
// pastDueSince = now - 3d, gracePeriodEndsAt = pastDueSince + 7d (4 days remaining).
// Card on file is expired (exp 04/2025).

import {
  buildConsumptionEvents,
  buildFeatureBreakdown
} from './ai-credit-usage-fixtures';
import { Scenario, TENANT_ID_DEFAULT, daysFromNow } from './scenario.types';
import { SHARED_PLANS, SHARED_TOP_UP_PACKS } from './shared-plans';

const PRICE_PER_SEAT_MINOR = 1500;   // FOUNDATION USD monthly
const SEATS = 5;
const SUBTOTAL_MINOR = PRICE_PER_SEAT_MINOR * SEATS;
const TOTAL_MINOR = SUBTOTAL_MINOR;

export const pastDueUs: Scenario = {
  name: 'past_due_us',
  subscription: {
    id: 'sub_past_due_us',
    stripeSubscriptionId: null,
    tenantId: TENANT_ID_DEFAULT,
    status: 'past_due',
    tier: 'FOUNDATION',
    billingPeriod: 'monthly',
    currency: 'USD',
    countryCode: 'US',
    seats: SEATS,
    pricePerSeatMinor: PRICE_PER_SEAT_MINOR,
    subtotalMinor: SUBTOTAL_MINOR,
    discountMinor: 0,
    taxMinor: 0,
    totalMinor: TOTAL_MINOR,
    appliedCoupon: null,
    currentPeriodStart: daysFromNow(-33),
    currentPeriodEnd: daysFromNow(-3),
    cancelAtPeriodEnd: false,
    canceledAt: null,
    pastDueSince: daysFromNow(-3),
    gracePeriodEndsAt: daysFromNow(4)
  },
  plans: SHARED_PLANS,
  invoices: [
    {
      id: 'inv_us_pd_001',
      number: 'INV-2026-0050',
      status: 'open',
      issuedAt: daysFromNow(-3),
      paidAt: null,
      periodStart: daysFromNow(-3),
      periodEnd: daysFromNow(27),
      currency: 'USD',
      subtotalMinor: SUBTOTAL_MINOR,
      discountMinor: 0,
      taxMinor: 0,
      taxRate: null,
      taxBasis: null,
      totalMinor: TOTAL_MINOR,
      hostedInvoiceUrl: 'https://invoice.stripe.com/mock/inv_us_pd_001',
      invoicePdfUrl: 'https://invoice.stripe.com/mock/inv_us_pd_001.pdf',
      lineItems: [
        {
          description: 'Foundation plan x 5 seats',
          kind: 'subscription',
          quantity: SEATS,
          unitAmountMinor: PRICE_PER_SEAT_MINOR,
          amountMinor: SUBTOTAL_MINOR,
          periodStart: daysFromNow(-3),
          periodEnd: daysFromNow(27)
        }
      ]
    }
  ],
  receipts: [],
  paymentMethod: {
    id: 'pm_us_expired',
    type: 'card',
    brand: 'visa',
    last4: '0019',
    expMonth: 4,
    expYear: 2025,
    isDefault: true
  },
  aiCreditBalance: {
    tenantId: TENANT_ID_DEFAULT,
    // S8: static ISO dates so the toast dedup key is stable across midnight UTC.
    billingCycleStart: '2026-04-20T00:00:00.000Z',
    billingCycleEnd: '2026-05-20T00:00:00.000Z',
    // Per #08 spec: 2,000 allocated / 1,700 used / 15% remaining -> fires 20%
    // threshold toast on first balance emission.
    allocatedCredits: 2000,
    usedCredits: 1700,
    reservedCredits: 0,
    topUpBalance: 0,
    remainingCredits: 2000 - 1700,
    basicTurnsAllocated: 2500,
    basicTurnsUsed: 800
  },
  topUpPacks: SHARED_TOP_UP_PACKS,
  consumptionEvents: buildConsumptionEvents({
    cycleAllocated: 2000,
    currentUsed: 1700,
    topUpBalance: 0,
    idPrefix: 'past_due_us',
    seeds: [
      { featureKey: 'ai_chat', creditsConsumed: 220, modelTier: 'premium', hoursAgo: 100, requestSummary: "Chat: 'support thread'" },
      { featureKey: 'document_extraction', creditsConsumed: 180, modelTier: 'premium', hoursAgo: 92, requestSummary: 'Extract receipt' },
      { featureKey: 'summarization', creditsConsumed: 80, modelTier: 'basic', hoursAgo: 84, requestSummary: 'Summarize finance thread' },
      { featureKey: 'ai_chat', creditsConsumed: 140, modelTier: 'premium', hoursAgo: 76, requestSummary: "Chat: 'planning notes'" },
      { featureKey: 'document_extraction', creditsConsumed: 200, modelTier: 'premium', hoursAgo: 64, requestSummary: 'Extract invoice fields' },
      { featureKey: 'image_generation', creditsConsumed: 180, modelTier: 'premium', hoursAgo: 56, requestSummary: 'Generate promo image' },
      { featureKey: 'ai_chat', creditsConsumed: 110, modelTier: 'premium', hoursAgo: 48, requestSummary: "Chat: 'pricing'" },
      { featureKey: 'ai_chat', creditsConsumed: 90, modelTier: 'basic', hoursAgo: 40, requestSummary: "Chat: 'how-to'" },
      { featureKey: 'summarization', creditsConsumed: 70, modelTier: 'basic', hoursAgo: 32, requestSummary: 'Summarize meeting notes' },
      { featureKey: 'image_generation', creditsConsumed: 240, modelTier: 'premium', hoursAgo: 26, requestSummary: 'Generate banner' },
      { featureKey: 'document_extraction', creditsConsumed: 160, modelTier: 'premium', hoursAgo: 20, requestSummary: 'Extract receipt' },
      { featureKey: 'ai_chat', creditsConsumed: 120, modelTier: 'premium', hoursAgo: 14, requestSummary: "Chat: 'campaign'" },
      { featureKey: 'ai_chat', creditsConsumed: 100, modelTier: 'premium', hoursAgo: 9, requestSummary: "Chat: 'onboarding draft'" },
      { featureKey: 'image_generation', creditsConsumed: 150, modelTier: 'premium', hoursAgo: 5, requestSummary: 'Generate ad image' },
      { featureKey: 'ai_chat', creditsConsumed: 90, modelTier: 'premium', hoursAgo: 1, requestSummary: "Chat: 'follow-up'" }
    ]
  }),
  featureUsageBreakdown: [],
  topUpHistory: []
};

(pastDueUs.featureUsageBreakdown as unknown) = buildFeatureBreakdown(
  pastDueUs.consumptionEvents ?? []
);
